import { TemplateImport } from "app/models/templateImport"
import { ConflictStatus, PatchTimeslotPayload, PostTimeSlotPayload } from "app/models/timeslot"
import { Params } from "app/redux/planningTimeslots/planningTimeslots.reducer"
import { AxiosPromise } from "axios"
import Code, { Categories, postCodeData } from "app/models/code"
import { IPostContractApiPayload } from "app/models/contract"
import PostBalanceHoursCorrectionPayload from "app/models/correction"
import { Employee } from "app/models/employee"
import { PlanningExportParams } from "app/redux/planningParams/types"
import { TPostShiftPayload } from "app/redux/shifts/shifts.actions"
import Shift from "app/models/shift"
import HrGeneralLeave, { PostHrGeneralLeave } from "app/models/hrGeneralLeave"
import HrUnavailability, {
  HrUnavailabilityQueryParams,
  PatchHrUnavailability,
  PostHrUnavailability,
} from "app/models/hrUnavailability"
import { WorkingHoursPutPayload } from "app/models/user"
import WorkingHour from "app/models/workingHour"
import { IPostLeaveEntitlementPayload, IPatchLeaveEntitlementsPayload } from "app/models/hrLeaveEntitlement"
import Practice from "app/models/practice"
import { planningApi } from "./api"

export const getTimeslots = (params: Params): AxiosPromise =>
  planningApi("/timeslots", {
    method: "GET",
    params,
  })

export const getTimeslotsCsv = (params: PlanningExportParams): AxiosPromise<string> =>
  planningApi("/timeslots.csv", {
    method: "GET",
    params,
  })

export const getTimeslotsSdWorx = (params: PlanningExportParams): AxiosPromise<string> =>
  planningApi("/export_dat", {
    method: "GET",
    params,
  })

type TPostTimeslotsPayload = PostTimeSlotPayload & { employee: Employee }
export const postTimeslots = (payload: { data: TPostTimeslotsPayload }): AxiosPromise =>
  planningApi("/timeslots", {
    data: payload.data,
    method: "POST",
  })

export const patchTimeslots = (id: number, payload: { data: PatchTimeslotPayload }): AxiosPromise =>
  planningApi(`/timeslots/${id}`, {
    data: payload.data,
    method: "PATCH",
  })

export const deleteTimeslotsById = (id: number): AxiosPromise =>
  planningApi(`timeslots/${id}`, {
    method: "DELETE",
  })

export const importTemplate = (payload: { data: TemplateImport }): AxiosPromise =>
  planningApi("/timeslots/import", {
    data: payload.data,
    method: "POST",
  })

export const getWeeks = (week: string): AxiosPromise =>
  planningApi("/weeks", {
    method: "GET",
    params: { week },
  })

export const getWeekAvailabilities = (params: { week: string; sort?: string[] }): AxiosPromise =>
  planningApi(`/weeks/${params.week}/availabilities`, {
    method: "GET",
    params,
  })

export const getAvailabilities = (params: {
  date: string
  workstationId: string
  functions: string[]
  from: string
  to: string
  sort?: string[]
}): AxiosPromise =>
  planningApi("/availabilities", {
    method: "GET",
    params,
  })

export const postAvailabilitiesCodes = (data: Code): AxiosPromise =>
  planningApi("/hr_codes", {
    data: {
      category: data.category,
      code: data.code,
      title: data.title,
      order: data.order,
    },
    method: "POST",
  })

export const patchAvailabilityCodes = (data: Code): AxiosPromise => {
  const { category, code, deletable, disabled, title } = data
  return planningApi(`/hr_codes/${data.id}`, {
    data: {
      category,
      code,
      deletable,
      disabled,
      title,
    },
    method: "PATCH",
  })
}

export const patchAvailabilityCodeOrder = (
  id: number,
  data: { order: number },
  params: {
    category: Categories
  },
): AxiosPromise =>
  planningApi(`/hr_codes/${id}/order`, {
    data,
    params,
    method: "PATCH",
  })

export const patchConflicts = (timeslotId: number, conflictId: number, status: ConflictStatus): AxiosPromise =>
  planningApi(`/timeslots/${timeslotId}/conflicts/${conflictId}`, {
    data: {
      status,
    },
    method: "PATCH",
  })

export const getBalanceHours = (
  employeeId: string,
  params: { weekId?: number; comparison?: string; limit?: number },
): AxiosPromise =>
  planningApi(`/employees/${employeeId}/balance_hours`, {
    method: "GET",
    params,
  })

export const getBalanceHoursCorrections = (employeeId: string, params?: { sort?: string[] }): AxiosPromise =>
  planningApi(`/employees/${employeeId}/balance_hours_corrections`, {
    method: "GET",
    params,
  })

export const postBalanceHoursCorrection = (employeeId: string, data: PostBalanceHoursCorrectionPayload) =>
  planningApi(`/employees/${employeeId}/balance_hours_corrections`, {
    method: "POST",
    data,
  })

export const postContract = (employeeId: string, payload: IPostContractApiPayload) =>
  planningApi(`/employees/${employeeId}/contracts`, {
    method: "POST",
    data: payload,
  })

export const getContracts = (params?: { week?: string; sort?: string[] }): AxiosPromise =>
  planningApi(`/contracts/`, {
    method: "GET",
    params,
  })

export const getContractsByEmployeeId = (employeeId: string, params?: { week?: string }): AxiosPromise =>
  planningApi(`/employees/${employeeId}/contracts`, {
    method: "GET",
    params,
  })

export const getShifts = (): AxiosPromise => planningApi("/shifts")

export const postShift = (shift: TPostShiftPayload): AxiosPromise =>
  planningApi(`/shifts`, { data: shift, method: "POST" })

export const putShift = (id: string, shift: Shift): AxiosPromise =>
  planningApi(`/shifts/${id}`, {
    data: shift,
    method: "PUT",
  })

export const deleteShift = (id: string): AxiosPromise => planningApi(`/shifts/${id}`, { method: "DELETE" })

export const getHrGeneralLeaves = (params?: { from: string; to: string }): AxiosPromise =>
  planningApi(`/hr_general_leaves`, {
    method: "GET",
    params,
  })

export const postHrGeneralLeaves = (data: PostHrGeneralLeave[]): AxiosPromise =>
  planningApi(`/hr_general_leaves`, {
    method: "POST",
    data,
  })

export const deleteHrGeneralLeaves = (data: HrGeneralLeave[]): AxiosPromise =>
  planningApi(`/hr_general_leaves`, {
    method: "DELETE",
    data,
  })

export const getHrUnavailabilities = (params?: HrUnavailabilityQueryParams): AxiosPromise<HrUnavailability[]> =>
  planningApi(`/hr_unavailabilities`, {
    method: "GET",
    params,
  })

export const postHrUnavailabilities = (data: PostHrUnavailability[]): AxiosPromise =>
  planningApi(`/hr_unavailabilities`, {
    method: "POST",
    data,
  })

export const patchHrUnavailabilities = (data: PatchHrUnavailability[]): AxiosPromise =>
  planningApi(`/hr_unavailabilities`, {
    method: "PATCH",
    data,
  })
export const getUserAbsences = (params: { uid: string; year: string; leaves?: boolean }): AxiosPromise =>
  planningApi("/hr_year_overview", {
    method: "GET",
    params: {
      year: params.year,
      employee_id: params.uid,
      categories: params.leaves ? ["leave"] : ["absence"],
    },
  })

export const getCodes = (): AxiosPromise => planningApi("/hr_codes")

export const getCodesByCategory = (params: { category: Categories; disabled?: boolean }): AxiosPromise =>
  planningApi("/hr_codes", { params, method: "GET" })

export const postCodes = (data: postCodeData): AxiosPromise =>
  planningApi("/hr_codes", {
    data: {
      category: data.category,
      code: data.code,
      title: data.title,
      order: data.order,
    },
    method: "POST",
  })

export const patchCodes = (data: Code): AxiosPromise => {
  const { category, code, deletable, disabled, title } = data
  return planningApi(`/hr_codes/${data.id}`, {
    data: {
      category,
      code,
      deletable,
      disabled,
      title,
    },
    method: "PATCH",
  })
}

export const deleteCodes = (id: string): AxiosPromise =>
  planningApi(`/hr_codes/${id}`, {
    method: "DELETE",
  })

export const patchCodeOrder = (
  id: number,
  data: { order: number },
  params: {
    category: Categories
  },
): AxiosPromise =>
  planningApi(`/hr_codes/${id}/order`, {
    data,
    params,
    method: "PATCH",
  })

export const putTimetablesById = (payload: WorkingHoursPutPayload): AxiosPromise => {
  const { data, userId } = payload
  return planningApi(`/employees/${userId}/timetables`, {
    method: "PUT",
    data,
  })
}

export const getTimetables = (id: string): AxiosPromise => planningApi(`/employees/${id}/timetables`, { method: "GET" })

export const deleteTimetablesById = (userId: string, data: WorkingHour): AxiosPromise => {
  const { id: deleteId } = data
  return planningApi(`/employees/${userId}/timetables/${deleteId}`, {
    method: "DELETE",
  })
}

export const getHrLeaveEntitlements = (payload?: {
  employee_id: string
  sort?: string[]
  year?: number
  category?: Categories
  hr_code_id?: number
}): AxiosPromise =>
  planningApi(`/hr_leave_entitlements_overview`, {
    method: "GET",
    params: payload,
  })

export const postHrLeaveEntitlements = (payload: IPostLeaveEntitlementPayload): AxiosPromise =>
  planningApi(`/hr_leave_entitlements`, {
    method: "POST",
    data: payload,
  })

export const patchHrLeaveEntitlements = (
  payload: Omit<IPatchLeaveEntitlementsPayload, "employee_id">,
  id: number,
): AxiosPromise =>
  planningApi(`/hr_leave_entitlements/${id}`, {
    method: "PATCH",
    data: payload,
  })

export const deleteHrLeaveEntitlements = (hrLeaveEntitlementId: number): AxiosPromise =>
  planningApi(`/hr_leave_entitlements/${hrLeaveEntitlementId}`, {
    method: "DELETE",
  })

export const getHrUnavailabilitiesOverview = (payload?: {
  employee_id?: string
  start_date?: string
  end_date?: string
  hr_code_id?: number
}): AxiosPromise =>
  planningApi("/hr_unavailabilities_overview", {
    method: "GET",
    params: payload,
  })

export const getPractice = (): AxiosPromise => planningApi("/current_practice")
export const getPractices = (): AxiosPromise => planningApi("/practices")
export const putPractice = (data: Practice): AxiosPromise =>
  planningApi("/current_practice", {
    data,
    method: "PUT",
  })

export const putPracticeFeatures = (data: Practice["features"]): AxiosPromise =>
  planningApi("/practice/features", {
    data,
    method: "PUT",
  })
